import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { services, contactInfo } from '@/mocks/homeData';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setDropdownOpen(false);
  }, [location]);

  const isTransparent = !scrolled && isHome;

  const scrollTo = (id: string) => {
    setMobileOpen(false);
    setDropdownOpen(false);
    if (!isHome) {
      window.location.href = `/#${id}`;
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const navServices = services.slice(0, 5);

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        isTransparent
          ? 'bg-transparent'
          : 'bg-background-50/95 backdrop-blur-md shadow-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16 md:h-20">
        <Link
          to="/"
          className="flex items-center gap-2 cursor-pointer"
        >
          <span
            className={`font-heading text-xl md:text-2xl font-bold transition-colors duration-500 ${
              isTransparent ? 'text-white' : 'text-foreground-950'
            }`}
          >
            Dra. Marihana Pasquali
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-8">
          <Link
            to="/"
            className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
              isTransparent ? 'text-white/90 hover:text-white' : 'text-foreground-700 hover:text-foreground-950'
            } ${!isHome ? '' : 'hidden'}`}
          >
            Início
          </Link>

          <div
            className="relative"
            onMouseEnter={() => setDropdownOpen(true)}
            onMouseLeave={() => setDropdownOpen(false)}
          >
            <button
              onClick={() => isHome ? scrollTo('servicos') : window.location.href = '/#servicos'}
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1 ${
                isTransparent ? 'text-white/90 hover:text-white' : 'text-foreground-700 hover:text-foreground-950'
              }`}
            >
              Serviços
              <i className={`ri-arrow-down-s-line text-xs transition-transform duration-300 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {dropdownOpen && (
              <div className="absolute top-full left-0 mt-2 w-72 bg-background-50 rounded-xl shadow-lg border border-background-200 py-2 z-50">
                {services.map((service) => (
                  <Link
                    key={service.id}
                    to={service.slug}
                    className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors cursor-pointer"
                  >
                    <span className="w-8 h-8 flex items-center justify-center bg-accent-50 rounded-lg flex-shrink-0">
                      <i className={`${service.icon} text-accent-600 text-xs`}></i>
                    </span>
                    <span className="leading-tight">{service.title}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>

          <button
            onClick={() => scrollTo('diferenciais')}
            className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
              isTransparent ? 'text-white/90 hover:text-white' : 'text-foreground-700 hover:text-foreground-950'
            }`}
          >
            Diferenciais
          </button>

          <button
            onClick={() => scrollTo('depoimentos')}
            className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
              isTransparent ? 'text-white/90 hover:text-white' : 'text-foreground-700 hover:text-foreground-950'
            }`}
          >
            Depoimentos
          </button>

          <button
            onClick={() => scrollTo('faq')}
            className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
              isTransparent ? 'text-white/90 hover:text-white' : 'text-foreground-700 hover:text-foreground-950'
            }`}
          >
            FAQ
          </button>

          <button
            onClick={() => scrollTo('localizacao')}
            className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
              isTransparent ? 'text-white/90 hover:text-white' : 'text-foreground-700 hover:text-foreground-950'
            }`}
          >
            Contato
          </button>

          <Link
            to="/convite"
            className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
              isTransparent ? 'text-white/90 hover:text-white' : 'text-foreground-700 hover:text-foreground-950'
            }`}
          >
            Convite
          </Link>
        </div>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={`https://wa.me/${contactInfo.whatsapp}?text=Olá!%20Gostaria%20de%20agendar%20uma%20avaliação.`}
            target="_blank"
            rel="nofollow noopener noreferrer"
            className={`whitespace-nowrap text-sm font-medium transition-all duration-300 cursor-pointer ${
              isTransparent
                ? 'text-white/90 hover:text-white'
                : 'text-foreground-700 hover:text-foreground-950'
            }`}
          >
            <i className="ri-whatsapp-line mr-1"></i>
            WhatsApp
          </a>
          <button
            onClick={() => scrollTo('cta')}
            className="whitespace-nowrap bg-primary-500 text-white text-sm font-semibold px-6 py-2.5 rounded-full hover:bg-primary-600 transition-all duration-300 cursor-pointer"
          >
            Agendar Consulta
          </button>
        </div>

        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className={`lg:hidden w-10 h-10 flex items-center justify-center cursor-pointer transition-colors duration-500 ${
            isTransparent ? 'text-white' : 'text-foreground-950'
          }`}
        >
          <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-background-50 border-t border-background-200 animate-fade-in-up">
          <div className="px-4 py-4 flex flex-col gap-2 max-h-[70vh] overflow-y-auto">
            <Link
              to="/"
              className="text-foreground-700 text-sm font-medium py-3 cursor-pointer hover:text-primary-500 transition-colors"
            >
              Início
            </Link>
            <div className="text-xs uppercase tracking-wider text-foreground-400 font-semibold px-1 pt-2 pb-1">
              Serviços
            </div>
            {services.map((service) => (
              <Link
                key={service.id}
                to={service.slug}
                className="flex items-center gap-3 text-foreground-700 text-sm py-2.5 cursor-pointer hover:text-primary-500 transition-colors pl-2"
              >
                <i className={`${service.icon} text-accent-500 text-sm`}></i>
                {service.title}
              </Link>
            ))}
            <hr className="border-background-200 my-2" />
            <button
              onClick={() => scrollTo('diferenciais')}
              className="text-foreground-700 text-sm font-medium py-3 cursor-pointer hover:text-primary-500 transition-colors text-left"
            >
              Diferenciais
            </button>
            <button
              onClick={() => scrollTo('depoimentos')}
              className="text-foreground-700 text-sm font-medium py-3 cursor-pointer hover:text-primary-500 transition-colors text-left"
            >
              Depoimentos
            </button>
            <button
              onClick={() => scrollTo('faq')}
              className="text-foreground-700 text-sm font-medium py-3 cursor-pointer hover:text-primary-500 transition-colors text-left"
            >
              FAQ
            </button>
            <button
              onClick={() => scrollTo('localizacao')}
              className="text-foreground-700 text-sm font-medium py-3 cursor-pointer hover:text-primary-500 transition-colors text-left"
            >
              Contato
            </button>
            <Link
              to="/convite"
              className="text-foreground-700 text-sm font-medium py-3 cursor-pointer hover:text-primary-500 transition-colors text-left"
            >
              Convite
            </Link>
            <div className="flex flex-col gap-2 pt-3">
              <a
                href={`https://wa.me/${contactInfo.whatsapp}`}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="whitespace-nowrap text-center bg-[#25D366] text-white text-sm font-semibold px-6 py-3 rounded-full cursor-pointer"
              >
                <i className="ri-whatsapp-line mr-1"></i>
                WhatsApp
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}